import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useAuth } from "@/contexts/auth-context";
import { supabase } from "@/utils/supabase";
import { CartItem, Gender, Size, StoreProduct } from "@/utils/types";
import {
  clearCart,
  getCart,
  removeFromCart,
  saveCart,
  updateCartItem,
} from "@/utils/cart-storage";

type StoreCartItem = CartItem & { category: string };

export default function CheckoutPage() {
  const [cartItems, setCartItems] = useState<StoreCartItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deliveryRequested, setDeliveryRequested] = useState(false);
  const [deliveryName, setDeliveryName] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    const cart = getCart();
    if (!cart) {
      setCartItems([]);
      return;
    }

    setCartItems(
      cart.items.filter((item) => item.category === "Store") as StoreCartItem[]
    );
  };

  const handleQuantityChange = (
    productId: number,
    size: Size,
    gender: Gender,
    newQuantity: number,
    variant?: string | null
  ) => {
    if (newQuantity <= 0) {
      handleRemoveItem(productId, size, gender, variant);
      return;
    }

    updateCartItem(productId, size, gender, newQuantity, variant);
    loadCart();
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const handleRemoveItem = (
    productId: number,
    size: Size,
    gender: Gender,
    variant?: string | null
  ) => {
    removeFromCart(productId, size, gender, variant);
    loadCart();
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const handleClearCart = () => {
    if (!confirm("Are you sure you want to clear your cart?")) {
      return;
    }

    const cart = getCart();
    if (!cart) {
      setCartItems([]);
      return;
    }

    const remainingItems = cart.items.filter(
      (item) => item.category !== "Store"
    );
    if (remainingItems.length === 0) {
      clearCart();
    } else {
      saveCart({ items: remainingItems, updatedAt: new Date().toISOString() });
    }

    loadCart();
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const storeSubtotal = useMemo(
    () =>
      cartItems.reduce(
        (sum, item) => sum + Number(item.price) * item.quantity,
        0
      ),
    [cartItems]
  );

  // Shipping no longer costs anything through the club — customers who want
  // their order shipped provide their own prepaid label.
  const storeTotal = storeSubtotal;

  const handleSubmitOrder = async () => {
    if (!user) {
      setSubmitError("You must be logged in to submit an order.");
      return;
    }

    if (cartItems.length === 0) {
      setSubmitError("Your cart is empty.");
      return;
    }

    if (
      deliveryRequested &&
      (!deliveryName.trim() || !deliveryAddress.trim())
    ) {
      setSubmitError(
        "Please provide a name and full address for shipping, or turn shipping off to pick up in person."
      );
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const variantIds = cartItems
        .map((item) => item.variant)
        .filter((variant): variant is string => Boolean(variant))
        .map((variant) => Number.parseInt(variant, 10));

      if (
        variantIds.length !== cartItems.length ||
        variantIds.some(Number.isNaN)
      ) {
        throw new Error(
          "One or more cart items are missing a valid variant ID."
        );
      }

      const { data: catalogRows, error: catalogError } = await supabase
        .from("vittoria_store")
        .select(
          "id, store_id, price, quantity, name, currency, product_id, variant_id, sku, img_reference, product_url, product_item_info, available_bool"
        )
        .is("order_id", null)
        .is("order_item_id", null)
        .in("variant_id", variantIds);

      if (catalogError) {
        throw catalogError;
      }

      const catalogByVariantId = new Map(
        ((catalogRows || []) as StoreProduct[]).map((row) => [
          row.variant_id?.toString(),
          row,
        ])
      );

      for (const item of cartItems) {
        const product = catalogByVariantId.get(item.variant || "");

        if (!product) {
          throw new Error(
            `Product ${item.productName} is no longer available.`
          );
        }

        if (product.available_bool !== true) {
          throw new Error(`${item.productName} is no longer available.`);
        }

        if (
          product.quantity !== null &&
          product.quantity !== undefined &&
          item.quantity > product.quantity
        ) {
          throw new Error(
            `${item.productName} only has ${product.quantity} item(s) left in stock.`
          );
        }
      }

      const { data: order, error: orderError } = await supabase
        .from("orders")
        .insert({
          user_id: user.id,
          user_email: user.email,
          total_price: storeTotal,
          currency: "EUR",
          status: "pending",
          delivery_requested: deliveryRequested,
          delivery_name: deliveryRequested ? deliveryName.trim() : null,
          delivery_address: deliveryRequested ? deliveryAddress.trim() : null,
        })
        .select()
        .single();

      if (orderError) {
        throw orderError;
      }

      const orderItemsData = cartItems.map((item) => ({
        order_id: order.id,
        variant_id: item.variant ? Number.parseInt(item.variant, 10) : null,
        product_name: item.productName,
        quantity: item.quantity,
        price_at_time: item.price,
        currency: item.currency || "EUR",
      }));

      const { data: insertedOrderItems, error: orderItemsError } =
        await supabase
          .from("order_items")
          .insert(orderItemsData)
          .select("id, variant_id");

      if (orderItemsError) {
        await supabase.from("orders").delete().eq("id", order.id);
        throw orderItemsError;
      }

      const orderItemsByVariantId = new Map(
        (insertedOrderItems || []).map((item) => [
          item.variant_id?.toString(),
          item.id,
        ])
      );

      const linkedStoreRows = cartItems.map((item) => {
        const product = catalogByVariantId.get(item.variant || "");
        const orderItemId = orderItemsByVariantId.get(item.variant || "");

        if (!product || !orderItemId) {
          throw new Error(
            `Failed to link ${item.productName} into store rows.`
          );
        }

        return {
          store_id: product.store_id,
          order_id: order.id,
          order_item_id: orderItemId,
          price: item.price,
          quantity: item.quantity,
          name: item.productName,
          currency: item.currency || "EUR",
          available_bool: product.available_bool,
          img_reference: product.img_reference,
          product_url: product.product_url,
          product_id: product.product_id,
          variant_id: product.variant_id,
          sku: product.sku,
          product_item_info: product.product_item_info,
        };
      });

      const { error: linkedRowsError } = await supabase
        .from("vittoria_store")
        .insert(linkedStoreRows);

      if (linkedRowsError) {
        await supabase.from("orders").delete().eq("id", order.id);
        throw linkedRowsError;
      }

      const inventoryUpdates = cartItems.map(async (item) => {
        const product = catalogByVariantId.get(item.variant || "");
        if (
          !product ||
          product.quantity === null ||
          product.quantity === undefined
        ) {
          return;
        }

        const nextQuantity = Math.max(product.quantity - item.quantity, 0);

        const { error: updateError } = await supabase
          .from("vittoria_store")
          .update({
            quantity: nextQuantity,
            available_bool: nextQuantity > 0,
          })
          .eq("id", product.id);

        if (updateError) {
          throw updateError;
        }
      });

      await Promise.all(inventoryUpdates);

      const cart = getCart();
      if (cart) {
        const remainingItems = cart.items.filter(
          (item) => item.category !== "Store"
        );
        if (remainingItems.length === 0) {
          clearCart();
        } else {
          saveCart({
            items: remainingItems,
            updatedAt: new Date().toISOString(),
          });
        }
      }

      window.dispatchEvent(new Event("cartUpdated"));
      window.dispatchEvent(new Event("orderSubmitted"));
      navigate(`/dashboard/store/confirmation?orderId=${order.id}`);
    } catch (err: any) {
      console.error("Error submitting order:", err);
      setSubmitError(
        err.message || "Failed to submit order. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Checkout</h1>
          <p className="text-gray-600 mb-6">
            Your cart is empty or contains no store products.
          </p>
          <Link
            to="/dashboard/store"
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Store
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <Link
          to="/dashboard/store"
          className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Store
        </Link>
        <h1 className="text-3xl font-bold text-gray-800">Checkout</h1>
        <p className="text-gray-600">Review your order</p>
      </div>

      {submitError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{submitError}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Order Summary</h2>

        <div className="space-y-4">
          {cartItems.map((item) => (
            <div
              key={`${item.productId}-${item.variant}`}
              className="flex items-center justify-between py-4 border-b border-gray-200 last:border-b-0"
            >
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">
                  {item.productName}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  €{item.price.toFixed(2)} each
                </p>
                {item.variant && (
                  <p className="text-xs text-gray-500 mt-1">
                    Variant ID: {item.variant}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      handleQuantityChange(
                        item.productId,
                        item.size,
                        item.gender,
                        item.quantity - 1,
                        item.variant
                      )
                    }
                    className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm transition-colors"
                  >
                    −
                  </button>
                  <span className="text-sm font-semibold text-gray-800 w-8 text-center">
                    {item.quantity}
                  </span>
                  <button
                    onClick={() =>
                      handleQuantityChange(
                        item.productId,
                        item.size,
                        item.gender,
                        item.quantity + 1,
                        item.variant
                      )
                    }
                    className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm transition-colors"
                  >
                    +
                  </button>
                </div>
                <span className="text-sm font-semibold text-gray-800 w-20 text-right">
                  €{(item.price * item.quantity).toFixed(2)}
                </span>
                <button
                  onClick={() =>
                    handleRemoveItem(
                      item.productId,
                      item.size,
                      item.gender,
                      item.variant
                    )
                  }
                  className="text-red-600 hover:text-red-900 ml-4"
                  title="Remove item"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200 space-y-2">
          <div className="flex justify-between items-center text-gray-600">
            <span>Subtotal:</span>
            <span>€{storeSubtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center text-gray-600">
            <span>Shipping:</span>
            <span>{deliveryRequested ? "Self-shipped" : "Pickup"}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="text-lg font-bold text-gray-800">Total:</span>
            <span className="text-lg font-bold text-purple-600">
              €{storeTotal.toFixed(2)}
            </span>
          </div>
          <p className="text-xs text-gray-500 text-right">
            Price already includes VAT
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Shipping</h2>

        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="fulfillment-method"
              checked={!deliveryRequested}
              onChange={() => setDeliveryRequested(false)}
              className="mt-1 h-4 w-4 border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">Picking up in person</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="fulfillment-method"
              checked={deliveryRequested}
              onChange={() => setDeliveryRequested(true)}
              className="mt-1 h-4 w-4 border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">
              Shipping — I'll print my own shipping label
            </span>
          </label>
        </div>

        {deliveryRequested && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={deliveryName}
                onChange={(e) => setDeliveryName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Address *
              </label>
              <textarea
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Street, house number, postal code, city, country"
              />
            </div>
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
              <p className="text-sm text-yellow-800">
                📦 You'll need to arrange and print your own prepaid shipping
                label for this address. Once you have it, send it to{" "}
                <strong>Jan Wagebach</strong> via WhatsApp so your order can be
                shipped.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Payment Instructions */}
      <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
        <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">
          Payment Instructions
        </h2>
        <p className="text-sm sm:text-base text-gray-700">
          You will receive an invoice via WhatsApp. Please pay the total amount
          according to the invoice once you receive it.
        </p>
      </div>

      {/* Next Steps */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
        <h3 className="text-sm sm:text-base font-semibold text-gray-800 mb-2">
          What happens next?
        </h3>
        <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm text-gray-700">
          <li>Invoice will be provided with final items and amount.</li>
          <li>Invoice is to be payed after it has been received.</li>
        </ul>
      </div>

      <div className="flex justify-between items-center">
        <button
          onClick={handleClearCart}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Clear Cart
        </button>

        <button
          onClick={handleSubmitOrder}
          disabled={submitting}
          className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? "Submitting Order..." : "Submit Order"}
        </button>
      </div>
    </div>
  );
}
